import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminProfileApi, type MyProfile } from '@api/client';
import { StringListEditor } from '@components/admin/StringListEditor';

type Field = 'name' | 'lastName' | 'phone' | 'jobTitle' | 'bio' | 'websiteUrl';

interface FormState {
  name: string;
  lastName: string;
  phone: string;
  jobTitle: string;
  bio: string;
  credentials: string[];
  functions: string[];
  websiteUrl: string;
}

function toForm(p: MyProfile | undefined): FormState {
  return {
    name: p?.name ?? '',
    lastName: p?.lastName ?? '',
    phone: p?.phone ?? '',
    jobTitle: p?.jobTitle ?? '',
    bio: p?.bio ?? '',
    credentials: p?.credentials ?? [],
    functions: p?.functions ?? [],
    websiteUrl: p?.websiteUrl ?? '',
  };
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function cleanStringArray(values: string[]): string[] | null {
  const out = values.map((v) => v.trim()).filter((v) => v.length > 0);
  return out.length === 0 ? null : out;
}

export function MyProfileCard() {
  const qc = useQueryClient();

  const { data: profile, isLoading } = useQuery<MyProfile>({
    queryKey: ['profile', 'me'],
    queryFn: () => adminProfileApi.get(),
  });

  const [form, setForm] = useState<FormState>(toForm(undefined));
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setForm(toForm(profile));
  }, [profile]);

  const mutation = useMutation({
    mutationFn: () =>
      adminProfileApi.update({
        name: emptyToNull(form.name),
        lastName: emptyToNull(form.lastName),
        phone: emptyToNull(form.phone),
        jobTitle: emptyToNull(form.jobTitle),
        bio: emptyToNull(form.bio),
        credentials: cleanStringArray(form.credentials),
        functions: cleanStringArray(form.functions),
        websiteUrl: emptyToNull(form.websiteUrl),
      }),
    onSuccess: () => {
      setSavedMsg('Cambios guardados.');
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] });
      window.setTimeout(() => { setSavedMsg(null); }, 2500);
    },
  });

  const uploadPhoto = useMutation({
    mutationFn: (file: File) => adminProfileApi.uploadPhoto(file),
    onSuccess: () => {
      setPhotoErr(null);
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] });
    },
    onError: (e) => {
      setPhotoErr(e instanceof Error ? e.message : 'No se pudo subir la foto.');
    },
  });

  const deletePhoto = useMutation({
    mutationFn: () => adminProfileApi.deletePhoto(),
    onSuccess: () => {
      setPhotoErr(null);
      void qc.invalidateQueries({ queryKey: ['profile', 'me'] });
    },
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setPhotoErr('La foto debe pesar menos de 2 MB.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setPhotoErr('Formato no soportado. Usa JPG, PNG o WebP.');
      return;
    }
    uploadPhoto.mutate(file);
  }

  function update(key: Field, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  if (isLoading) return <div className="card">Cargando perfil…</div>;

  const photoUrl = profile?.photoUrl ?? null;

  return (
    <div className="card" style={{ display: 'grid', gap: 16 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Mi perfil</h2>
        <span style={{ color: 'var(--muted, #6b7280)', fontSize: 13 }}>
          Estos datos son tuyos. El nombre se usa para personalizar saludos y, si
          eres responsable de un centro o staff visible, para mostrarte en la web pública.
        </span>
        {profile?.email && (
          <span style={{ color: 'var(--muted, #6b7280)', fontSize: 13 }}>
            Sesión iniciada como <strong>{profile.email}</strong>
          </span>
        )}
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            background: photoUrl
              ? `center/cover no-repeat url(${photoUrl})`
              : 'var(--surface-2, #f1f5f4)',
            border: '1px solid var(--border, #e5e7eb)',
            flexShrink: 0,
          }}
          aria-label={photoUrl ? 'Foto de perfil' : 'Sin foto de perfil'}
        />
        <div style={{ display: 'grid', gap: 6 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onPickFile}
            style={{ display: 'none' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploadPhoto.isPending}
            >
              {uploadPhoto.isPending
                ? 'Subiendo…'
                : photoUrl
                ? 'Cambiar foto'
                : 'Subir foto'}
            </button>
            {photoUrl && (
              <button
                type="button"
                className="btn"
                onClick={() => { deletePhoto.mutate(); }}
                disabled={deletePhoto.isPending}
              >
                {deletePhoto.isPending ? 'Eliminando…' : 'Eliminar'}
              </button>
            )}
          </div>
          <span style={{ color: 'var(--muted, #6b7280)', fontSize: 12 }}>
            JPG, PNG o WebP. Máx 2 MB.
          </span>
          {photoErr && (
            <span style={{ color: 'var(--danger, #b91c1c)', fontSize: 12 }}>{photoErr}</span>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Labeled label="Nombre">
          <input
            className="input"
            type="text"
            maxLength={200}
            value={form.name}
            onChange={(e) => { update('name', e.target.value); }}
            placeholder="Tu nombre"
          />
        </Labeled>
        <Labeled label="Apellidos">
          <input
            className="input"
            type="text"
            maxLength={200}
            value={form.lastName}
            onChange={(e) => { update('lastName', e.target.value); }}
            placeholder="Tus apellidos"
          />
        </Labeled>
        <Labeled label="Teléfono personal">
          <input
            className="input"
            type="tel"
            maxLength={200}
            value={form.phone}
            onChange={(e) => { update('phone', e.target.value); }}
            placeholder="+34 600 000 000"
          />
        </Labeled>
        <Labeled label="Cargo / rol mostrado">
          <input
            className="input"
            type="text"
            maxLength={200}
            value={form.jobTitle}
            onChange={(e) => { update('jobTitle', e.target.value); }}
            placeholder="Ej.: Directora clínica"
          />
        </Labeled>
      </div>

      <Labeled label="Bio">
        <textarea
          className="input"
          rows={3}
          maxLength={200}
          value={form.bio}
          onChange={(e) => { update('bio', e.target.value); }}
          placeholder="Una breve descripción profesional (máx. 200 caracteres)"
        />
      </Labeled>

      <Labeled label="Credenciales / títulos">
        <StringListEditor
          value={form.credentials}
          onChange={(next) => { setForm((f) => ({ ...f, credentials: next })); }}
          singular="credencial"
          placeholder="Ej.: Grado en Nutrición Humana y Dietética"
          emptyLabel="Sin credenciales aún."
        />
      </Labeled>

      <Labeled label="Funciones (chips de marketing)">
        <StringListEditor
          value={form.functions}
          onChange={(next) => { setForm((f) => ({ ...f, functions: next })); }}
          singular="función"
          placeholder="Ej.: Consulta Dietética, Dirección, Coordinación…"
          emptyLabel="Sin funciones aún."
        />
      </Labeled>

      <Labeled label="Página web personal">
        <input
          className="input"
          type="url"
          maxLength={1024}
          value={form.websiteUrl}
          onChange={(e) => { update('websiteUrl', e.target.value); }}
          placeholder="https://…"
        />
      </Labeled>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          className="btn btn-primary"
          onClick={() => { mutation.mutate(); }}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
        </button>
        {savedMsg && (
          <span style={{ color: 'var(--success, #15803d)', fontSize: 13 }}>{savedMsg}</span>
        )}
        {mutation.isError && (
          <span style={{ color: 'var(--danger, #b91c1c)', fontSize: 13 }}>
            No se pudieron guardar los cambios. Vuelve a intentarlo.
          </span>
        )}
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--muted, #6b7280)' }}>{label}</span>
      {children}
    </label>
  );
}
